import { useEffect, useState } from "react";
import {
  adminGetCaseStudies, adminUpsertCaseStudy, adminDeleteCaseStudy,
  adminUploadCaseOnePager, adminDeleteCaseOnePager, formatApiErrorDetail,
} from "@/lib/api";
import {
  Plus, Trash2, Save, X, ChevronUp, ChevronDown, Eye, EyeOff,
  Upload, Paperclip, FileText,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_SUGGESTIONS = [
  "Digital health", "Enterprise", "Consumer app", "Wellness", "Seasonal startup",
  "Nonprofit · Salesforce", "Government", "Fintech", "Mid-market",
];

const EMPTY = {
  type: "", headline: "", body: "", sector_label: "", the_work: "", timeframe: "",
  metric_line: "", pull_line: "", profile: "", problem: "",
  what_we_did: "", outcome: "", order: 99, published: true,
};

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // record or {} for new
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems(await adminGetCaseStudies()); }
    catch { toast.error("Could not load case studies."); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY, order: (items.length + 1) });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.type.trim()) { toast.error("Client type is required."); return; }
    setBusy(true);
    try {
      await adminUpsertCaseStudy(editing);
      toast.success("Saved. The Work page updates immediately.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const togglePublished = async (c) => {
    try {
      await adminUpsertCaseStudy({ ...c, published: !c.published });
      toast.success(c.published ? "Hidden from the Work page." : "Published to the Work page.");
      load();
    } catch { toast.error("Could not update."); }
  };

  const reorder = async (c, dir) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === c.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx], b = sorted[swapIdx];
    try {
      await adminUpsertCaseStudy({ ...a, order: b.order });
      await adminUpsertCaseStudy({ ...b, order: a.order });
      load();
    } catch { toast.error("Could not reorder."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the ${c.type} case study? This cannot be undone.`)) return;
    try { await adminDeleteCaseStudy(c.id); toast.success("Deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  const sorted = [...items].sort((a, b) => a.order - b.order);

  return (
    <div data-testid="admin-case-studies">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Case studies</p>
          <h1 className="cv-display text-3xl mt-4">The work, fully editable.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-lg">
            Each case is a record. Publish, reorder, or edit any field and the Work page updates immediately.
          </p>
        </div>
        <button data-testid="case-new-btn" onClick={startNew}
          className="cv-btn-primary inline-flex items-center gap-2 self-start whitespace-nowrap">
          <Plus size={15} /> New case study
        </button>
      </div>

      {editing && (
        <CaseForm value={editing} onChange={setEditing} onSave={save} onCancel={cancel} busy={busy} />
      )}

      <div className="mt-8 divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
        {sorted.map((c, i) => (
          <div key={c.id} data-testid="admin-case-row" className="py-4 flex items-center gap-4">
            <div className="flex flex-col">
              <button onClick={() => reorder(c, "up")} disabled={i === 0}
                className="text-[#9A938A] hover:text-[#1F1B16] disabled:opacity-30" aria-label="Move up">
                <ChevronUp size={15} />
              </button>
              <button onClick={() => reorder(c, "down")} disabled={i === sorted.length - 1}
                className="text-[#9A938A] hover:text-[#1F1B16] disabled:opacity-30" aria-label="Move down">
                <ChevronDown size={15} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="cv-h2 text-sm">{c.type}</p>
                {c.has_one_pager && (
                  <span className="inline-flex items-center gap-1 text-[#515E4F]" title="One-pager attached">
                    <Paperclip size={12} />
                  </span>
                )}
              </div>
              <p className="cv-body text-xs text-[#9A938A] mt-0.5 truncate">
                {c.headline || c.metric_line || c.pull_line || "(no headline yet)"}
              </p>
            </div>
            <button data-testid="case-publish-toggle" onClick={() => togglePublished(c)}
              className={`inline-flex items-center gap-1.5 cv-sans uppercase tracking-[0.1em] text-[0.6rem] px-2.5 py-1 rounded-full border transition-colors ${
                c.published
                  ? "border-[#515E4F] text-[#515E4F]"
                  : "border-[rgba(28,27,25,0.3)] text-[#9A938A]"
              }`}>
              {c.published ? <Eye size={12} /> : <EyeOff size={12} />}
              {c.published ? "Published" : "Hidden"}
            </button>
            <button onClick={() => setEditing(c)} className="cv-link text-xs">Edit</button>
            <button onClick={() => remove(c)} className="text-[#A65A43] hover:text-[#8a4836]" aria-label="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {sorted.length === 0 && (
          <p className="cv-body text-sm text-[#9A938A] py-6">No case studies yet.</p>
        )}
      </div>
    </div>
  );
}

function CaseForm({ value, onChange, onSave, onCancel, busy }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!value.id) { toast.error("Save the case study first, then attach a one-pager."); return; }
    setUploading(true);
    try {
      const res = await adminUploadCaseOnePager(value.id, file);
      onChange({ ...value, one_pager_name: res.one_pager_name, has_one_pager: true });
      toast.success("One-pager attached. It now appears in Resources.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const removeFile = async () => {
    try {
      await adminDeleteCaseOnePager(value.id);
      onChange({ ...value, one_pager_name: null, has_one_pager: false });
      toast.success("One-pager removed.");
    } catch { toast.error("Could not remove."); }
  };

  return (
    <div data-testid="case-form" className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] overflow-hidden">
      <div className="cv-hairline-copper" />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Client type * (drives the filter chip)">
            <input data-testid="case-type" className="cv-input" list="case-types"
              value={value.type} onChange={(e) => set("type", e.target.value)} />
            <datalist id="case-types">
              {TYPE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
            </datalist>
          </Field>
          <Field label="Order">
            <input data-testid="case-order" type="number" className="cv-input"
              value={value.order} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} />
          </Field>
        </div>
        <Field label="Headline * (Fraunces; lead with the outcome or problem, never a timeframe)">
          <input data-testid="case-headline" className="cv-input"
            placeholder="e.g. An MVP without giving up the company."
            value={value.headline} onChange={(e) => set("headline", e.target.value)} />
        </Field>
        <Field label="Body (2–4 sentences: Problem → Caliber → Outcome. No client names, no confidential metrics)">
          <textarea data-testid="case-body" rows={4} className="cv-input resize-none"
            value={value.body} onChange={(e) => set("body", e.target.value)} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Detail · Sector">
            <input data-testid="case-sector-label" className="cv-input" placeholder="e.g. digital health"
              value={value.sector_label} onChange={(e) => set("sector_label", e.target.value)} />
          </Field>
          <Field label="Detail · The work">
            <input data-testid="case-thework" className="cv-input" placeholder="e.g. product strategy, architecture"
              value={value.the_work} onChange={(e) => set("the_work", e.target.value)} />
          </Field>
          <Field label="Detail · Timeframe (optional)">
            <input data-testid="case-timeframe" className="cv-input" placeholder="e.g. 12 weeks"
              value={value.timeframe} onChange={(e) => set("timeframe", e.target.value)} />
          </Field>
        </div>

        <div className="border-t border-[rgba(28,27,25,0.12)] pt-4">
          <p className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">
            Read the case (deeper detail, shown when expanded)
          </p>
        </div>
        <Field label="Metric line (legacy; optional reference)">
          <input data-testid="case-metric" className="cv-input" placeholder="e.g. 12 weeks · $0 venture funding · 100% equity kept"
            value={value.metric_line} onChange={(e) => set("metric_line", e.target.value)} />
        </Field>
        <Field label="Pull-line (legacy one-line summary; optional)">
          <textarea data-testid="case-pull" rows={2} className="cv-input resize-none"
            value={value.pull_line} onChange={(e) => set("pull_line", e.target.value)} />
        </Field>
        <Field label="Profile">
          <textarea data-testid="case-profile" rows={2} className="cv-input resize-none"
            value={value.profile} onChange={(e) => set("profile", e.target.value)} />
        </Field>
        <Field label="The problem">
          <textarea data-testid="case-problem" rows={2} className="cv-input resize-none"
            value={value.problem} onChange={(e) => set("problem", e.target.value)} />
        </Field>
        <Field label="What we did">
          <textarea data-testid="case-didwhat" rows={3} className="cv-input resize-none"
            value={value.what_we_did} onChange={(e) => set("what_we_did", e.target.value)} />
        </Field>
        <Field label="Outcome">
          <textarea data-testid="case-outcome" rows={2} className="cv-input resize-none"
            value={value.outcome} onChange={(e) => set("outcome", e.target.value)} />
        </Field>

        {/* One-pager */}
        <div className="border-t border-[rgba(28,27,25,0.12)] pt-4">
          <p className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">
            Resources one-pager (optional)
          </p>
          {value.has_one_pager || value.one_pager_name ? (
            <div className="mt-2 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 cv-body text-sm text-[#1F1B16]/80">
                <FileText size={15} className="text-[#515E4F]" /> {value.one_pager_name || "Attached"}
              </span>
              <button onClick={removeFile} className="cv-link text-xs text-[#A65A43]">Remove</button>
            </div>
          ) : (
            <label className={`mt-2 inline-flex items-center gap-2 cv-btn-secondary cursor-pointer ${!value.id ? "opacity-50" : ""}`}>
              {uploading ? "Uploading..." : <><Upload size={14} /> Attach file</>}
              <input data-testid="case-onepager-input" type="file" className="hidden"
                disabled={!value.id || uploading} onChange={onFile}
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" />
            </label>
          )}
          {!value.id && (
            <p className="cv-body text-xs text-[#9A938A] mt-1.5">Save the case study first to attach a file.</p>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button data-testid="case-published-toggle" type="button" onClick={() => set("published", !value.published)}
            className="inline-flex items-center gap-2.5">
            <span className={`w-9 h-5 rounded-full border transition-colors relative ${value.published ? "bg-[#A6633C] border-[#A6633C]" : "bg-transparent border-[rgba(28,27,25,0.3)]"}`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${value.published ? "left-4 bg-[#F6F1E9]" : "left-0.5 bg-[#9A938A]"}`} />
            </span>
            <span className="cv-sans uppercase tracking-[0.1em] text-[0.68rem] text-[#1F1B16]/70">Published</span>
          </button>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button data-testid="case-save" onClick={onSave} disabled={busy}
            className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {busy ? "Saving..." : "Save case study"}
          </button>
          <button onClick={onCancel} className="cv-btn-secondary inline-flex items-center gap-2">
            <X size={14} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
